"use client";

import React from "react";
import { Play, Pause } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Waveform } from "@/components/ui/tls/waveform";
import { playGlyphOffset } from "@/lib/optical-alignment";
import { playToggleA11y } from "@/lib/a11y/play-toggle";

export interface WaveformBandProps {
  peaks: number[];
  /** Playback position as a percentage (0-100); only shown while playing. */
  progress: number;
  isPlaying: boolean;
  isLoading: boolean;
  /** Post-mount flag from the parent; gates the waveform vs SSR placeholder. */
  mounted: boolean;
  /** Sound title, used for the play/pause/loading aria-labels. */
  title: string;
  onPlayPause: (e?: React.MouseEvent) => void;
  /** An active preview promotes its row into the full-width signal plate. */
  expanded?: boolean;
}

/**
 * Pure presentation: the card's full-bleed waveform band with its centered
 * play/pause control. All state lives in the parent.
 */
export function WaveformBand({
  peaks,
  progress,
  isPlaying,
  isLoading,
  mounted,
  title,
  onPlayPause,
  expanded = false,
}: WaveformBandProps) {
  return (
    /* The button is the one accessible transport. The surrounding signal is
       deliberately non-interactive, avoiding a nested duplicate control. */
    <div className={expanded ? "sc-band is-expanded" : "sc-band"}>
      {mounted ? (
        <Waveform
          peaks={peaks}
          progress={isPlaying ? progress : 0}
          playColor="rgb(var(--sc-cat))"
          height={44}
        />
      ) : (
        // SSR placeholder
        <div style={{ height: 44, width: "100%", background: "var(--tls-border-subtle)", borderRadius: 4 }} />
      )}

      {/* Centered play / pause control. Hidden during playback so the
          waveform progress reads unobstructed; returns as a pause control
          on band hover. Tapping the band toggles playback for touch users
          (no hover). */}
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          onPlayPause();
        }}
        // NOT `disabled` while loading: the browser blurs a focused element the
        // moment it becomes disabled, so a keyboard user who pressed play would
        // lose focus to <body> and never hear the resulting state change
        // (same failure mode as TES-1579 on the detail page). `aria-busy`
        // conveys buffering without making the control unfocusable.
        data-icon-btn
        // Playback state is exposed via aria-pressed with a stable name (TES-1581).
        {...playToggleA11y({ playing: isPlaying, title, loading: isLoading })}
        className="sc-play tlsr-focus"
        // Size lives in the .sc-play CSS (44 at d1, 52 at d0 mobile and in the
        // expanded plate) so the density axis can scale the control without
        // an inline width outranking it.
        style={{
          padding: 0,
          borderRadius: "50%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isLoading ? "wait" : "pointer",
          // DS PlayButton: at rest the control is a 1.5px ink RING with the
          // filled play triangle as the one filled glyph; playing = filled
          // accent circle with pause bars. Loading keeps the ring (spinners
          // are ink, loading is not an accent role).
          // Inside the expanded plate the ground is ink, so the ring and glyph
          // knock out to canvas instead.
          border: isPlaying
            ? "1.5px solid transparent"
            : `1.5px solid ${expanded ? "var(--tls-canvas)" : "var(--tls-fg-strong)"}`,
          background: isPlaying ? "rgb(var(--sc-cat))" : "transparent",
          // The brand foreground token passes ≥4.5:1 against the accent fill
          // in both themes; the resting glyph is plain ink.
          color: isPlaying
            ? "var(--tls-brand-fg)"
            : expanded
              ? "var(--tls-canvas)"
              : "var(--tls-fg-strong)",
        }}
      >
        {isLoading ? (
          <Spinner size="sm" className={expanded ? "text-[var(--tls-canvas)]" : "text-[var(--tls-fg-strong)]"} />
        ) : isPlaying ? (
          <Pause style={{ width: 18, height: 18, fill: "currentColor" }} aria-hidden="true" />
        ) : (
          <Play
            style={{ width: 18, height: 18, fill: "currentColor", marginLeft: playGlyphOffset(18) }}
            aria-hidden="true"
          />
        )}
      </button>
    </div>
  );
}
